import { ScaleBand } from 'd3-scale';
import { isUndefined } from 'lodash-es';

import { DataPoint } from '../../core/types/data-point.js';
import type { MinMax } from '../../core/types/min-max.js';
import { isBandScale } from '../../core/utils/detect-scale.js';
import { scaleBandInvert } from '../../core/utils/scale.js';
import type { XYChartLinearSelectionContextValue } from '../context/XYChartLinearSelection.context.js';
import {
  type MapDatapoints,
  type XYChartVariantsInternal,
} from '../types/xy-chart-internals.js';
import type {
  AxisSelectConfig,
  XYChartSelectionLinearMetadata,
} from '../types/xy-chart-selection.js';
import { XYAccessorDataTypes } from '../types/xy-chart.js';
import { getBandCenteredPoint } from '../utils/get-band-centered-point.js';
import { getCenterDate } from '../utils/get-center-date.js';
import { getCurrentScale } from '../utils/get-scale.js';
import { toAbsolute } from '../utils/value-representation.js';

const initialAxisSelectConfig: AxisSelectConfig = {
  uniqueXValuesWithDatapoints: new Set<XYAccessorDataTypes>(),
  datapointsPerXAxisValue: new Map<
    XYAccessorDataTypes,
    XYChartSelectionLinearMetadata[]
  >(),
};

const getPointValue = (
  point: XYChartSelectionLinearMetadata,
  datapointMetadataMap: MapDatapoints,
) => {
  const xValue = point.datapoint.x0 ?? point.datapoint.x1;

  if (xValue === undefined) {
    return undefined;
  }

  if (typeof xValue === 'string') {
    return xValue;
  }

  if (xValue instanceof Date) {
    const x1 = point.datapoint.x1;
    return x1 instanceof Date ? getCenterDate(xValue, x1) : xValue;
  }

  if (typeof xValue === 'number') {
    const metadata = datapointMetadataMap.get(point.datapoint);
    if (
      metadata?.baseline === 'y' &&
      typeof metadata.accumulator === 'number'
    ) {
      return xValue + metadata.accumulator;
    }
  }

  return xValue;
};

const getPointValues = (
  point: XYChartSelectionLinearMetadata,
  datapointMetadataMap: MapDatapoints,
): XYAccessorDataTypes[] => {
  const primaryValue = getPointValue(point, datapointMetadataMap);
  if (isUndefined(primaryValue)) {
    return [];
  }

  const { x0, x1 } = point.datapoint;

  if (typeof x0 === 'number' && typeof x1 === 'number' && x0 !== x1) {
    return [x0, x1];
  }

  return [primaryValue];
};

type SerializeDatapoint = Parameters<
  XYChartLinearSelectionContextValue['searchAllAxisDatapoints']
>[2];

export const useSortLinearDatapoints = (
  seriesVariantsProps: XYChartVariantsInternal[],
  datapointMetadataMap: MapDatapoints,
) => {
  const axisSelectConfig = new Map<string, AxisSelectConfig>();

  const getSortedValuesByAxisId = (axisId: string) => {
    const selectConfig = axisSelectConfig.get(axisId);
    if (selectConfig) {
      const { uniqueXValuesWithDatapoints } = selectConfig;
      return Array.from(uniqueXValuesWithDatapoints).sort((a, b) => {
        if (a instanceof Date && b instanceof Date) {
          return a.getTime() - b.getTime();
        }
        if (typeof a === 'number' && typeof b === 'number') {
          return a - b;
        }
        return 0;
      });
    }
    return [];
  };

  const handleAddValue = (point: XYChartSelectionLinearMetadata) => {
    const values = getPointValues(point, datapointMetadataMap);
    if (values.length === 0) {
      return;
    }

    const axisId = point.series.xAxisId;

    if (!axisSelectConfig.has(axisId)) {
      axisSelectConfig.set(axisId, {
        uniqueXValuesWithDatapoints: new Set<XYAccessorDataTypes>(),
        datapointsPerXAxisValue: new Map<
          XYAccessorDataTypes,
          XYChartSelectionLinearMetadata[]
        >(),
      });
    }

    const { uniqueXValuesWithDatapoints, datapointsPerXAxisValue } =
      axisSelectConfig.get(axisId) ?? initialAxisSelectConfig;

    for (const value of values) {
      if (!uniqueXValuesWithDatapoints.has(value)) {
        uniqueXValuesWithDatapoints.add(value);
        datapointsPerXAxisValue.set(value, []);
      }

      const existingPointsInXPosition =
        datapointsPerXAxisValue.get(value) ?? [];
      existingPointsInXPosition.push(point);
    }
  };

  const handleSearchNumericRange = (
    start: Omit<XYAccessorDataTypes, 'string'>,
    end: Omit<XYAccessorDataTypes, 'string'>,
    axisId: string,
  ) => {
    const sortedValues = getSortedValuesByAxisId(axisId);
    const [min, max] = start <= end ? [start, end] : [end, start];
    const startIndex = sortedValues.findIndex(
      (value) => value >= min && value <= max,
    );
    if (startIndex === -1) {
      return [];
    }
    const endIndex = sortedValues.findIndex((value) => value > max);
    const lastElement = endIndex === -1 ? sortedValues.length : endIndex;
    return sortedValues.slice(startIndex, lastElement);
  };

  const handleSearchCategoricalRange = (
    start: number,
    end: number,
    scale: ScaleBand<string>,
  ) => {
    const domainValues = scale.domain();
    const categoriesCoordinates = domainValues.map((d) =>
      getBandCenteredPoint(scale, d),
    );

    return categoriesCoordinates
      .filter((xValue) => xValue >= start && xValue <= end)
      .map((xValue) => scaleBandInvert(scale)(xValue));
  };

  const handleGetPoints = (
    axisId: string,
    range: XYAccessorDataTypes[],
    callback: SerializeDatapoint,
    pointsByRange: DataPoint[] = [],
  ) => {
    const selectConfig = axisSelectConfig.get(axisId);
    if (!selectConfig) {
      return pointsByRange;
    }

    const { datapointsPerXAxisValue } = selectConfig;

    const addMappedPoints = (mappedPoint: DataPoint | DataPoint[] | null) => {
      if (!mappedPoint) {
        return;
      }
      if (Array.isArray(mappedPoint)) {
        pointsByRange.push(...mappedPoint);
      } else {
        pointsByRange.push(mappedPoint);
      }
    };

    const mapPointsForValue = (points: XYChartSelectionLinearMetadata[]) => {
      for (const point of points) {
        addMappedPoints(callback(point));
      }
    };

    for (const value of range) {
      const points = datapointsPerXAxisValue.get(value);
      if (!points?.length) {
        continue;
      }
      mapPointsForValue(points);
    }
    return pointsByRange;
  };

  const handleGetPointsByCategories = (
    start: number,
    end: number,
    callback: SerializeDatapoint,
    axisId: string,
    scale: ScaleBand<string>,
    pointsByRange: DataPoint[] = [],
  ) => {
    const range = handleSearchCategoricalRange(start, end, scale);
    return handleGetPoints(axisId, range, callback, pointsByRange);
  };

  const handleGetPointsByNumericalRange = (
    start: number | Date,
    end: number | Date,
    callback: SerializeDatapoint,
    axisId: string,
    pointsByRange: DataPoint[] = [],
  ) => {
    const range = handleSearchNumericRange(start, end, axisId);
    return handleGetPoints(axisId, range, callback, pointsByRange);
  };

  const searchAllAxisDatapoints: XYChartLinearSelectionContextValue['searchAllAxisDatapoints'] =
    (
      start,
      end,
      callback,
      axesIds,
      axisIdToScales,
      axisIdToScaleDomain,
      axisIDToIsRelative,
    ) => {
      const axesCurrentSelection: Record<string, DataPoint[]> = {};
      const highlightedDatapoints: DataPoint[] = [];
      if (!axisIdToScales) {
        return { axesCurrentSelection, highlightedDatapoints };
      }
      const axesIdsArray = typeof axesIds === 'string' ? [axesIds] : axesIds;
      axesIdsArray.forEach((axisId) => {
        if (axisSelectConfig.has(axisId)) {
          const scale = getCurrentScale(axisIdToScales, axisId);
          if (isBandScale(scale)) {
            axesCurrentSelection[axisId] = handleGetPointsByCategories(
              start,
              end,
              callback,
              axisId,
              scale,
              highlightedDatapoints,
            );
          } else {
            let startDomain = scale.invert(start);
            let endDomain = scale.invert(end);
            if (axisIDToIsRelative.get(axisId)) {
              const domain = axisIdToScaleDomain.get(axisId) as MinMax;
              startDomain = toAbsolute(startDomain as number, domain);
              endDomain = toAbsolute(endDomain as number, domain);
            }
            axesCurrentSelection[axisId] = handleGetPointsByNumericalRange(
              startDomain,
              endDomain,
              callback,
              axisId,
              highlightedDatapoints,
            );
          }
        }
      });
      return { axesCurrentSelection, highlightedDatapoints };
    };

  return {
    getCurrentSelectConfig: () => axisSelectConfig,
    addValue: (point: XYChartSelectionLinearMetadata) => handleAddValue(point),
    searchAllAxisDatapoints,
  };
};
